import { useEffect, useState } from "react";

/**
 * One product, eight sections. The sidebar scopes to a product and each section
 * is a real route: #/p/<slug>/<section>. `#/` resolves to the first product's
 * dashboard once the portfolio loads.
 */
export const SECTIONS = ["dashboard", "attribution", "funnel", "pages", "demand", "scorecard", "health", "history"] as const;
export type Section = (typeof SECTIONS)[number];

export type AdminTab = "overview" | "accounts" | "users";

export type SettingsTab = "general" | "team" | "billing" | "pipeline";
export const SETTINGS_TABS: readonly SettingsTab[] = ["general", "team", "billing", "pipeline"] as const;

export type Route =
  | { name: "product"; slug: string | null; section: Section }
  | { name: "scorecardShare"; token: string }
  | { name: "newProduct" }
  | { name: "cards" }
  | { name: "pipeline" }
  | { name: "integrations"; kind?: string }
  | { name: "team" }
  | { name: "settings"; tab?: SettingsTab }
  | { name: "billing" }
  | { name: "admin"; tab: AdminTab; id?: string }
  | { name: "login" }
  | { name: "signup" }
  | { name: "verify"; token: string }
  | { name: "invite"; token: string }
  | { name: "reset"; token: string }
  | { name: "google"; token: string }
  | { name: "connected"; state: string };

const SYSTEM = ["cards", "pipeline", "integrations", "team", "billing", "login", "signup"] as const;

function parse(hash: string): Route {
  const h = hash.replace(/^#\/?/, "");
  const base = h.split("?")[0];
  if (base.startsWith("integrations/")) {
    const kind = decodeURIComponent(base.slice("integrations/".length));
    return { name: "integrations", kind: kind || undefined };
  }
  for (const name of SYSTEM) if (base === name) return { name };
  if (base === "settings") return { name: "settings", tab: "general" };
  if (base.startsWith("settings/")) {
    const sub = base.slice(9);
    const tab = (SETTINGS_TABS as readonly string[]).includes(sub) ? (sub as SettingsTab) : "general";
    return { name: "settings", tab };
  }
  if (base === "health") return { name: "pipeline" };
  if (base === "admin") return { name: "admin", tab: "overview" };
  if (base === "admin/users") return { name: "admin", tab: "users" };
  if (base === "admin/accounts") return { name: "admin", tab: "accounts" };
  if (base.startsWith("admin/a/")) {
    return { name: "admin", tab: "accounts", id: decodeURIComponent(base.slice(8)) };
  }
  if (base === "new-product") return { name: "newProduct" };
  if (base.startsWith("verify/")) return { name: "verify", token: decodeURIComponent(base.slice(7)) };
  if (base.startsWith("invite/")) return { name: "invite", token: decodeURIComponent(base.slice(7)) };
  if (base.startsWith("reset/")) return { name: "reset", token: decodeURIComponent(base.slice(6)) };
  if (base.startsWith("auth/google/")) return { name: "google", token: decodeURIComponent(base.slice(12)) };
  if (base.startsWith("connected/")) return { name: "connected", state: decodeURIComponent(base.slice(10)) };
  if (base.startsWith("scorecard/")) return { name: "scorecardShare", token: decodeURIComponent(base.slice(10)) };
  if (h.startsWith("p/")) {
    const [slug, section] = h.slice(2).split("/");
    if (slug) {
      return {
        name: "product",
        slug: decodeURIComponent(slug),
        section: (SECTIONS as readonly string[]).includes(section) ? (section as Section) : "dashboard",
      };
    }
  }
  return { name: "product", slug: null, section: "dashboard" };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parse(location.hash));
  useEffect(() => {
    const on = () => setRoute(parse(location.hash));
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return route;
}

export function go(route: Route): void {
  if (route.name === "product") {
    location.hash = route.slug
      ? `#/p/${encodeURIComponent(route.slug)}/${route.section}`
      : "#/";
  } else if (route.name === "verify") {
    location.hash = `#/verify/${encodeURIComponent(route.token)}`;
  } else if (route.name === "invite") {
    location.hash = `#/invite/${encodeURIComponent(route.token)}`;
  } else if (route.name === "reset") {
    location.hash = `#/reset/${encodeURIComponent(route.token)}`;
  } else if (route.name === "google") {
    location.hash = `#/auth/google/${encodeURIComponent(route.token)}`;
  } else if (route.name === "connected") {
    location.hash = `#/connected/${encodeURIComponent(route.state)}`;
  } else if (route.name === "scorecardShare") {
    location.hash = `#/scorecard/${encodeURIComponent(route.token)}`;
  } else if (route.name === "newProduct") {
    location.hash = "#/new-product";
  } else if (route.name === "integrations") {
    location.hash = route.kind
      ? `#/integrations/${encodeURIComponent(route.kind)}`
      : "#/integrations";
  } else if (route.name === "settings") {
    if (route.tab && route.tab !== "general") location.hash = `#/settings/${route.tab}`;
    else location.hash = "#/settings";
  } else if (route.name === "admin") {
    if (route.id) location.hash = `#/admin/a/${encodeURIComponent(route.id)}`;
    else if (route.tab === "users") location.hash = "#/admin/users";
    else if (route.tab === "accounts") location.hash = "#/admin/accounts";
    else location.hash = "#/admin";
  } else {
    location.hash = `#/${route.name}`;
  }
}
